import React from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import Search from '../app/Search/Search';
import Toolbar from '../app/Toolbar/Toolbar';

import TorgaRouter from '../torga/TorgaRouter';
import AppModuleConnector from '../utils/AppFramework/AppModuleConnector';

import { AppContext } from './AppContext';
import Console from './Console/Console';
import './MainRouter.css';
import { search } from './search';
import Admin from './Admin/Admin';
import UserSettings from './UserSettings/UserSettings';
import SideDrawer from './SideDrawer/SideDrawer';
import Login from './Login/Login';
import { clearAuthToken, getAuthToken, hasAuthToken } from './auth';

export const ObjectTypes = {};

export const DisplayModes = {
  COMPACT: 'compact',
  MOBILE: 'mobile',
  NORMAL: 'normal',
};

export default function MainRouter() {
  const [displayMode, setDisplayMode] = React.useState(DisplayModes.COMPACT);
  const [showMenu, setShowMenu] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(hasAuthToken());
  const [isCheckingSession, setIsCheckingSession] = React.useState(hasAuthToken());

  React.useEffect(() => {
    const resize = () => {
      if (window.innerWidth < 1194) {
        setDisplayMode(DisplayModes.MOBILE);
      } else {
        setDisplayMode(DisplayModes.COMPACT);
      }
    };
    resize();

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setIsCheckingSession(false);
      return;
    }

    fetch('/api/auth/me', {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unauthorized');
        }

        setIsAuthenticated(true);
      })
      .catch(() => {
        clearAuthToken();
        setIsAuthenticated(false);
      })
      .finally(() => setIsCheckingSession(false));
  }, [isAuthenticated]);

  const handleLogout = React.useCallback(async () => {
    try {
      const token = getAuthToken();
      await fetch('/api/auth/logout', {
        method: 'post',
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });
    } catch (_error) {
      // best-effort logout; local token reset below is authoritative
    } finally {
      clearAuthToken();
      setIsAuthenticated(false);
      setIsCheckingSession(false);
    }
  }, []);

  if (isCheckingSession) {
    return <div className="bp5-dark">Checking session...</div>;
  }

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={<Login onLoginSuccess={() => setIsAuthenticated(true)} />}
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  const mainrouter_content_visibility = !showMenu
    ? ''
    : 'mainrouter__content-visibility';

  return (
    <BrowserRouter>
      <div className="mainrouter bp5-dark">
        <div className="mainrouter__left" style={{ flexGrow: true }}>
          <Toolbar
            displayMode={displayMode}
            drawerClickHandler={() => {
              setShowMenu(!showMenu);
            }}
          />

          <SideDrawer
            show={displayMode !== DisplayModes.MOBILE || showMenu}
            compact={displayMode === DisplayModes.COMPACT}
            onLogout={handleLogout}
            toggleCompact={() => {
              if (displayMode === DisplayModes.COMPACT) {
                setDisplayMode(DisplayModes.NORMAL);
              } else if (displayMode === DisplayModes.NORMAL) {
                setDisplayMode(DisplayModes.COMPACT);
              }
            }}
          />
        </div>
        {
          <div
            className={[
              'mainrouter__content',
              mainrouter_content_visibility,
            ].join(' ')}
          >
            <Console />
            <div className="mainrouter__center">
              <AppModuleConnector
                context={AppContext}
                name="App"
                objectTypes={ObjectTypes}
                search={search}
              />

              <TorgaRouter />

              <Routes>
                <Route path="/" element={<div>Home</div>}></Route>
                <Route path="/admin" element={<Admin />} />
                <Route path="/settings" element={<UserSettings />} />
                <Route path="/search" element={<Search />} />
                <Route path="/*" element={<div></div>} />
              </Routes>
            </div>
            {/*<Footer />*/}
          </div>
        }
      </div>
    </BrowserRouter>
  );
}
